import { useState, useEffect, useCallback, useRef } from 'react';
import { incidentesDevolvidosDoOperador } from './incidentesApi';
import { notificar } from './notificacaoSistema';
import { criarNotificacao } from './notificacoesApi';

// ─────────────────────────────────────────────────────────────────────────────
// "UM TICKET SEU VOLTOU DO SETOR DE SISTEMAS."
//
// A correção ficou pronta, o suporte avançado devolveu o chamado e ele caiu na
// fila de retorno do operador — como qualquer agendamento, e é aí que estava o
// problema: na fila ele é um ticket igual aos outros, sem dizer que existe uma
// atualização esperando para ser aplicada no cliente.
//
// Este hook é o aviso. Ele consulta os incidentes devolvidos a ESTE operador e
// notifica uma vez por chamado, mesmo que a janela esteja minimizada — é o
// único canal do Orion que alcança quem não está olhando para ele (ver
// notificacaoSistema.js).
//
// UMA VEZ POR DEVOLUÇÃO, E A MEMÓRIA VAI PARA O DISCO. Em memória, recarregar a
// página faria o Orion anunciar de novo tudo o que já tinha anunciado — a mesma
// razão que levou os avisos da fila de retorno para o localStorage
// (services/avisosRetorno.js).
//
// O registro guarda QUANDO a devolução que foi anunciada aconteceu, e não um
// "já avisei deste": a mesma demanda pode voltar duas vezes: a correção não
// resolveu, o suporte devolve ao setor (o que limpa a marca, no Pulse), o
// desenvolvedor corrige de novo e o chamado volta ao operador. Na segunda vez
// a data é outra, e o aviso sai de novo — que é o ponto.
//
// A LISTA CONTINUA DISPONÍVEL depois do aviso. Notificação some; o retorno não
// se resolve sozinho. Quem precisa mostrá-lo na tela usa `retornos`.
// ─────────────────────────────────────────────────────────────────────────────

/** O mesmo passo das outras filas do Orion (ver useFilasMysuite). */
const INTERVALO_MS = 5 * 60 * 1000;

const PREFIXO = 'orion:retornos-sistemas';

/** Depois disto o registro é lixo: a demanda já foi tratada ou abandonada. */
const VALIDADE_DIAS = 30;

const chaveDe = (email) => `${PREFIXO}:${String(email || 'sem-email').toLowerCase()}`;

/**
 * Lê o registro: { [idDoIncidente]: instante da devolução anunciada, em ms }.
 *
 * Devolve objeto vazio em qualquer problema — armazenamento bloqueado, JSON
 * corrompido, janela anônima. Perder o registro faz o Orion avisar de novo, o
 * que é chato; travar por causa dele seria pior.
 */
function lerAvisados(email) {
  try {
    const bruto = window.localStorage.getItem(chaveDe(email));
    const dados = bruto ? JSON.parse(bruto) : {};
    return dados && typeof dados === 'object' ? dados : {};
  } catch (e) {
    return {};
  }
}

/**
 * Grava o registro, descartando o que virou lixo.
 *
 * `idsAtivos` são os retornos que ainda estão abertos AGORA, e nenhum deles é
 * descartado por idade: o valor guardado é a data da DEVOLUÇÃO, então um
 * chamado devolvido há mais de 30 dias e ainda não concluído cairia na faxina
 * e seria anunciado de novo no ciclo seguinte — e no seguinte, a cada cinco
 * minutos. A idade só limpa o que já saiu da lista.
 */
function gravarAvisados(email, dados, idsAtivos) {
  try {
    const corte = Date.now() - VALIDADE_DIAS * 24 * 60 * 60 * 1000;
    const ativos = new Set(idsAtivos || []);
    const limpo = Object.fromEntries(
      Object.entries(dados).filter(
        ([id, quando]) => ativos.has(id) || Number(quando) >= corte,
      ),
    );
    window.localStorage.setItem(chaveDe(email), JSON.stringify(limpo));
  } catch (e) {
    // Sem memória do aviso o pior que acontece é avisar de novo.
  }
}

/**
 * Quando esta devolução aconteceu, em ms. Zero quando a data não vem ou não
 * presta — aí o filtro compara 0 com o que está no disco e o aviso não repete.
 */
function instanteDaDevolucao(incidente) {
  const t = new Date(incidente?.devolvido_operador_em || '').getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** O texto da notificação. Separado para poder ser conferido sem subir a tela. */
export function textoDoRetorno(retornos) {
  if (!retornos?.length) return null;
  if (retornos.length === 1) {
    const r = retornos[0];
    const versao = r.nexus_versao ? ` (versão ${r.nexus_versao})` : '';
    return {
      titulo: `Ticket ${r.numero_ticket} voltou do setor de Sistemas`,
      corpo: `A correção está disponível${versao}. Agende a atualização com o cliente.`,
    };
  }
  return {
    titulo: `${retornos.length} tickets voltaram do setor de Sistemas`,
    corpo: `Correções disponíveis nos tickets ${retornos.map(r => r.numero_ticket).join(', ')}.`
      + ' Agende as atualizações com os clientes.',
  };
}

/**
 * Os retornos do setor de Sistemas para este operador.
 *
 * Devolve `{ retornos, carregando, recarregar }`. Sem e-mail não consulta nada:
 * é o e-mail que diz de quem é o chamado.
 */
export function useRetornoSistemas(email, codigo = null) {
  const [retornos, setRetornos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  // Evita duas notificações para o mesmo incidente quando duas consultas se
  // cruzam (o intervalo e um `recarregar` manual, por exemplo): o disco só é
  // lido no começo de cada ciclo, e sem isto as duas leriam o mesmo "ainda não
  // avisado".
  const anunciando = useRef(false);

  const consultar = useCallback(async () => {
    if (!email) { setRetornos([]); return; }
    setCarregando(true);
    try {
      const lista = await incidentesDevolvidosDoOperador(email);
      setRetornos(lista);

      if (anunciando.current || !lista.length) return;
      anunciando.current = true;
      try {
        const avisados = lerAvisados(email);
        const novos = lista.filter(i => instanteDaDevolucao(i) > Number(avisados[i.id] || 0));
        if (novos.length) {
          const texto = textoDoRetorno(novos);
          // `tag` fixa: um retorno novo substitui o aviso anterior na bandeja em
          // vez de empilhar três balões sobre o mesmo assunto.
          if (texto) notificar(texto.titulo, texto.corpo, { tag: 'orion-retorno-sistemas' });
          // Também na caixa do sininho, um por ticket — a bandeja some, a
          // caixa fica. A chave leva a data da devolução: a mesma devolução
          // não entra duas vezes, mas uma segunda devolução entra.
          for (const i of novos) {
            const um = textoDoRetorno([i]);
            criarNotificacao({
              destinatarioCodigo: codigo,
              destinatarioEmail: email,
              tipo: 'retorno_sistemas',
              titulo: um.titulo,
              texto: um.corpo,
              numeroTicket: i.numero_ticket,
              origem: 'Setor de Sistemas',
              chave: `retorno:${i.id}:${instanteDaDevolucao(i)}`,
            });
          }
          for (const i of novos) avisados[i.id] = instanteDaDevolucao(i) || Date.now();
          gravarAvisados(email, avisados, lista.map(i => i.id));
        }
      } finally {
        anunciando.current = false;
      }
    } finally {
      setCarregando(false);
    }
  }, [email, codigo]);

  useEffect(() => {
    consultar();
    const id = setInterval(consultar, INTERVALO_MS);
    return () => clearInterval(id);
  }, [consultar]);

  return { retornos, carregando, recarregar: consultar };
}
